import { ReminderType } from "../maintenance.enums";
import type {
  CreateMaintenanceWindowRequest,
  MaintenanceHistoryEntry,
  MaintenanceScheduleDraft,
} from "../types/maintenance.types";

const DATE_TIME_FORMATTER = new Intl.DateTimeFormat("en-US", {
  month: "short",
  day: "numeric",
  year: "numeric",
  hour: "numeric",
  minute: "2-digit",
});

type MaintenanceDateInput = Date | string | undefined;

const parseMaintenanceDateInput = (
  dateInput: MaintenanceDateInput,
): Date | null => {
  if (!dateInput) {
    return null;
  }

  const parsedDate = new Date(dateInput);

  return Number.isNaN(parsedDate.getTime()) ? null : parsedDate;
};

export const getMaintenanceDateTime = (
  date: MaintenanceDateInput,
  time: MaintenanceDateInput,
): Date | null => {
  const parsedDate = parseMaintenanceDateInput(date);
  const parsedTime = parseMaintenanceDateInput(time);

  if (!parsedDate || !parsedTime) {
    return null;
  }

  parsedDate.setHours(parsedTime.getHours(), parsedTime.getMinutes(), 0, 0);

  return parsedDate;
};

export const formatMaintenanceDateTime = (
  date: MaintenanceDateInput,
  time: MaintenanceDateInput,
  fallback: string,
): string => {
  const parsedDate = getMaintenanceDateTime(date, time);
  if (!parsedDate) {
    return fallback;
  }
  return DATE_TIME_FORMATTER.format(parsedDate);
};

export const getMaintenanceDurationLabel = (
  draft: MaintenanceScheduleDraft,
): string | null => {
  const startDate = getMaintenanceDateTime(draft.startDate, draft.startTime);
  const endDate = getMaintenanceDateTime(draft.endDate, draft.endTime);
  if (!startDate || !endDate || endDate <= startDate) {
    return null;
  }
  const hours = Math.round(
    (endDate.getTime() - startDate.getTime()) / 3_600_000,
  );
  return `${hours}h`;
};

export const getMaintenanceLeadTimeLabel = (
  draft: MaintenanceScheduleDraft,
): string | null => {
  const startDate = getMaintenanceDateTime(draft.startDate, draft.startTime);
  const reminderDate = getMaintenanceDateTime(
    draft.reminderDate,
    draft.reminderTime,
  );
  if (!startDate || !reminderDate || reminderDate >= startDate) {
    return null;
  }
  const hours = Math.round(
    (startDate.getTime() - reminderDate.getTime()) / 3_600_000,
  );
  return `${hours}h`;
};

export const buildCreateMaintenancePayload = (
  draft: MaintenanceScheduleDraft,
): CreateMaintenanceWindowRequest => {
  const combinedStart = getMaintenanceDateTime(
    draft.startDate,
    draft.startTime,
  );
  const combinedEnd = getMaintenanceDateTime(draft.endDate, draft.endTime);
  const combinedReminder = getMaintenanceDateTime(
    draft.reminderDate,
    draft.reminderTime,
  );

  const startIso = combinedStart?.toISOString() ?? "";

  return {
    startDate: startIso,
    startTime: startIso,
    endDate: combinedEnd?.toISOString(),
    endTime: combinedEnd?.toISOString(),
    reminderType: draft.reminderType,
    reminderDate:
      draft.reminderType === ReminderType.CUSTOM
        ? combinedReminder?.toISOString()
        : undefined,
    reminderTime:
      draft.reminderType === ReminderType.CUSTOM
        ? combinedReminder?.toISOString()
        : undefined,
    notice: draft.notice || undefined,
  };
};

const splitDateTime = (
  isoString: string | null,
): { date: Date; time: Date } | null => {
  if (!isoString) {
    return null;
  }
  const date = new Date(isoString);
  if (Number.isNaN(date.getTime())) {
    return null;
  }
  const time = new Date(date);
  return { date, time };
};

export const parseMaintenanceWindowToDraft = (
  entry: MaintenanceHistoryEntry,
): MaintenanceScheduleDraft => {
  const start = splitDateTime(entry.startAt);
  const end = splitDateTime(entry.endAt);
  const reminder = splitDateTime(entry.reminderAt);

  return {
    startDate: start?.date ?? (undefined as unknown as Date),
    startTime: start?.time ?? (undefined as unknown as Date),
    endDate: end?.date,
    endTime: end?.time,
    reminderType: entry.reminderType,
    reminderDate: reminder?.date,
    reminderTime: reminder?.time,
    notice: entry.notice ?? "",
  };
};
